"use client";

import {
  TCollabComment,
  TCollabThread,
  TiptapCollabProvider,
} from "@hocuspocus/provider";
import { subscribeToThreads } from "@tiptap-pro/extension-comments";
import { Editor } from "@tiptap/react";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuthContext } from "../../context/auth-context";

interface WriterThreadsContextValue {
  threads: TCollabThread[];
  selectedThreadId: string | null;
  comments: TCollabComment[];
  thread: TCollabThread | null;
  createThread: (content: string) => void;
  addComment: (threadId: string, content: string) => void;
  selectThread: (threadId: string | null) => void;
  resolveThread: (threadId: string) => void;
}

const WriterThreadsContext = createContext<WriterThreadsContextValue | null>(
  null,
);

interface WriterThreadsProviderProps {
  children: ReactNode;
  editor: Editor | null;
  provider: TiptapCollabProvider | null;
  selectedThreadId: string | null;
}

export function WriterThreadsProvider({
  children,
  editor,
  provider,
  selectedThreadId,
}: WriterThreadsProviderProps) {
  const { authDetails } = useAuthContext();
  const [threads, setThreads] = useState<TCollabThread[]>([]);

  // Subscribe to threads changes
  useEffect(() => {
    if (!provider) return;

    return subscribeToThreads({
      provider,
      callback: setThreads,
    });
  }, [provider]);

  const thread =
    threads.find(
      (thread) => thread.id === selectedThreadId && !thread.resolvedAt,
    ) ?? null;

  const comments = thread?.comments || [];

  const createThread = useCallback(
    (content: string) => {
      if (!editor || !authDetails?.name) return;

      editor
        .chain()
        .focus()
        .setThread({
          content,
          commentData: { userName: authDetails.name },
        })
        .run();
    },
    [editor, authDetails?.name],
  );

  const addComment = useCallback(
    (threadId: string, content: string) => {
      if (!provider || !authDetails?.name) return;

      provider.addComment(threadId, {
        content,
        data: { userName: authDetails.name },
      });
    },
    [provider, authDetails?.name],
  );

  const selectThread = useCallback(
    (threadId: string | null) => {
      if (!editor) return;

      if (threadId) {
        editor.commands.selectThread({
          id: threadId,
          focus: true,
          scrollIntoView: true,
        });
      } else {
        editor.commands.unselectThread();
      }
    },
    [editor],
  );

  const resolveThread = useCallback(
    (threadId: string) => {
      if (!editor) return;

      editor.commands.resolveThread({ id: threadId });

      if (selectedThreadId === threadId) {
        selectThread(null);
      }
    },
    [editor, selectThread, selectedThreadId],
  );

  const value: WriterThreadsContextValue = {
    threads,
    selectedThreadId,
    comments,
    thread,
    createThread,
    addComment,
    selectThread,
    resolveThread,
  };

  return (
    <WriterThreadsContext.Provider value={value}>
      {children}
    </WriterThreadsContext.Provider>
  );
}

export function useWriterThreadsContext() {
  const context = useContext(WriterThreadsContext);
  if (!context) {
    throw new Error(
      "useWriterThreadsContext must be used within a WriterThreadsProvider",
    );
  }
  return context;
}
